// Diagnostics suppressor for the IntelliSense Engine.
// PRD: Slice "Diagnostics Suppression Rewiring" — `filterDiagnostics`
// drops SSTS / pgls markers that are known false positives or that the
// SQL parser flags as low-confidence.
//
// Two dialect-level patterns are codified:
//   • MSSQL: SSTS misreports `OPENJSON(...) WITH (...)` inside a CTE body.
//   • PostgreSQL: pgls misreports `RETURNING` inside a CTE DML body.
//
// Plus a structural rule (R3): a marker that overlaps a parser error-recovery
// span (ERROR or MISSING) is suppressed unless the safety rail kicks in.
//
// Safety rail: an Error-severity marker is preserved when the resolver has
// confidently bound the marker range — i.e. the parse is clean at that
// span AND the snapshot contains an identifier matching the source text in
// that span. This protects real LSP errors about already-known identifiers
// from being incorrectly suppressed.
//
// GRA-7 Slice A3: the R3 rule used to walk a tree-sitter `Tree` for ERROR
// nodes. It now consumes `FilterDiagnosticsContext.errorRecoveryView` —
// the same projection shape `EditorContext.errorRecoveryView` carries —
// computed by the caller from `ParsedDocument.recovery.errorRanges` and
// `recovery.missingRanges`. No tree-sitter dependency at this boundary.

import type { DatabaseEngineId, SchemaSnapshot } from '@/core/engines';
import { compareVersions } from '@/features/intellisense/versionGating/resolveCompat';
import type { SqlRange } from '@/features/sql-grammar/model';

import { classifyPgqStatementForSuppression } from './pgqSubset';

/**
 * Minimal projection of `monaco.editor.IMarkerData` so this module has no
 * Monaco runtime dependency. Coordinates are 1-based per Monaco convention.
 */
export type IMarkerData = {
  readonly startLineNumber: number;
  readonly startColumn: number;
  readonly endLineNumber: number;
  readonly endColumn: number;
  readonly message: string;
  readonly severity: number;
  readonly source?: string;
  readonly code?: string;
};

/** A parser error-recovery span: where the parser couldn't fit a clean
 *  grammar rule. Confidence in [0,1]: 1.0 means the parser is certain this
 *  span is erroneous (matches `recovery.errorRanges`); 0.5 means a MISSING
 *  span (matches `recovery.missingRanges`). Mirrors `EditorContext.errorRecoveryView`. */
export type ErrorRecoveryEntry = {
  readonly range: SqlRange;
  readonly confidence: number;
};

export type FilterDiagnosticsContext = {
  /** Error-recovery view of the buffer (parser ERROR + MISSING ranges with
   *  confidence weights). `null` when the parser hasn't been loaded yet
   *  (e.g. early page load) or the engine has no grammar fork — pass-through
   *  behavior in that case. An empty array means "no errors", which still
   *  enables the safety-rail's confident-bind check. */
  readonly errorRecoveryView: ReadonlyArray<ErrorRecoveryEntry> | null;
  /** Schema bus snapshot for the buffer's connection. `null` when the cache
   *  is cold — disables the safety rail's identifier-confidence check. */
  readonly snapshot: SchemaSnapshot | null;
  /** Buffer source text. Required to map Monaco line/column to byte offset
   *  for overlap testing and source-text-pattern detection. */
  readonly source: string;
  readonly engineId: DatabaseEngineId;
  /** Server version string of the buffer's connection (e.g. `"19.0"`,
   *  `"16.4"`), threaded from the schema-bus snapshot's `serverVersion`.
   *  Drives R4's PG19-graph gate. Undefined when the snapshot is cold →
   *  R4 fails closed (no suppression). */
  readonly serverVersion?: string;
};

/** Monaco's `MarkerSeverity.Error` enum value. We avoid importing Monaco. */
const MARKER_SEVERITY_ERROR = 8;

/**
 * Drop markers the IntelliSense layer flags as known false positives. Pure
 * function — same input, same output. Caller-side wrapper hooks
 * `monaco.editor.setModelMarkers`.
 */
export function filterDiagnostics(
  markers: readonly IMarkerData[],
  context: FilterDiagnosticsContext,
): IMarkerData[] {
  if (markers.length === 0) return [];

  const lineStarts = computeLineStarts(context.source);

  return markers.filter((marker) => !shouldSuppress(marker, context, lineStarts));
}

function shouldSuppress(
  marker: IMarkerData,
  context: FilterDiagnosticsContext,
  lineStarts: readonly number[],
): boolean {
  const range = monacoRangeToOffsets(marker, lineStarts, context.source.length);
  if (!range) return false;

  const insideError = context.errorRecoveryView
    ? rangeOverlapsErrorRecovery(context.errorRecoveryView, range)
    : false;

  // Rule R4: pgls doesn't parse SQL/PGQ (PG19) yet — suppress its SYNTAX
  // markers on statements that (a) are PGQ, (b) our grammar parses cleanly
  // (no error-recovery overlap), (c) stay within the PG19-verified subset,
  // (d) on a server that actually supports PGQ (>= 19). Non-syntax
  // diagnostics are preserved (see `isPgqSyntaxMarker`).
  //
  // R4 deliberately runs BEFORE the safety rail (P1-T15, live-verified):
  // pgls emits its syntax diagnostics with a WHOLE-STATEMENT range, so any
  // realistic PGQ statement — which mentions snapshot-known schemas and
  // columns — counts as "confidently bound" and the rail would keep the
  // false marker forever. The rail protects narrow-range LSP errors from
  // R3's blanket overlap suppression; R4's own gates (syntax-class marker
  // + clean parse + verified subset + PGQ-capable server) are strictly
  // stronger evidence of a false positive than the rail's identifier
  // heuristic.
  if (
    context.engineId === 'pgsql' &&
    isPgqSyntaxMarker(marker) &&
    pgServerSupportsGraphs(context.serverVersion) &&
    !insideError &&
    classifyPgqStatementForSuppression(
      context.source,
      statementRangeCovering(context.source, range),
    ) === 'pgq-supported-subset'
  ) {
    return true;
  }

  // Safety rail: a real LSP error on a confidently-bound identifier must
  // never be suppressed. "Confidently bound" = parse is clean at the
  // range (no error-recovery overlap) AND the snapshot contains an
  // identifier whose lower-cased name matches the source text at this range.
  const confidentlyBound =
    !insideError &&
    context.snapshot !== null &&
    snapshotKnowsIdentifier(context.snapshot, context.source, range);

  if (marker.severity >= MARKER_SEVERITY_ERROR && confidentlyBound) {
    return false;
  }

  // Rule R1: MSSQL OPENJSON-in-CTE false positive.
  if (
    context.engineId === 'mssql' &&
    isInsideOpenJsonInsideCte(context.source, range)
  ) {
    return true;
  }

  // Rule R2: PostgreSQL RETURNING-in-CTE-DML false positive.
  if (
    context.engineId === 'pgsql' &&
    isInsideReturningInCteDml(context.source, range)
  ) {
    return true;
  }

  // Rule R3: tree-sitter ERROR overlap — low confidence in either direction.
  if (insideError) return true;

  return false;
}

// ─────────────────────────────────────────────────────────────────────────
// R4: PGQ subset gating helpers
// ─────────────────────────────────────────────────────────────────────────

/** Live pgls (verified against PG19 in propertyGraphEditorPgsql19.realDb
 *  .spec.ts) attaches the literal string `code: "syntax"` (with
 *  `source: "pg"`) to its syntax diagnostics — NOT a SQLSTATE — and emits no
 *  semantic diagnostics at all in this app's wiring. `undefined` and the
 *  syntax_error SQLSTATE (`42601`) stay accepted defensively for pgls
 *  variants that follow the LSP convention. Any other code — permission
 *  `42501`, undefined_table `42P01`, lint rule names, … — is treated as a
 *  semantic diagnostic and must be preserved, never masked by R4. */
function isPgqSyntaxMarker(marker: IMarkerData): boolean {
  return (
    marker.code === undefined ||
    marker.code === 'syntax' ||
    marker.code === '42601'
  );
}

/** True when `serverVersion`'s leading major is >= 19 (PG19 introduced
 *  SQL/PGQ). Undefined / unparseable → false (fail closed: no suppression
 *  when we can't confirm the server supports graphs).
 *
 *  `serverVersion` as threaded from the schema-bus snapshot is the FULL
 *  `SELECT version()` banner (e.g. `"PostgreSQL 19.0 (Debian 19.0-1.pgdg120+1)
 *  on x86_64-pc-linux-gnu, compiled by gcc ..."`), not a bare version number.
 *  Reuse the version-gating catalog's tolerant parser (`compareVersions`,
 *  which internally splits on runs of non-digits and reads the first
 *  digit-run as the major component) instead of a homegrown anchored regex —
 *  an `^\s*(\d+)` match against the banner returns null and fails closed
 *  permanently in production. */
function pgServerSupportsGraphs(serverVersion: string | undefined): boolean {
  if (!serverVersion) return false;
  return compareVersions(serverVersion, '19') >= 0;
}

/** Bounds the `;`-delimited statement segment covering `range.start`, so R4's
 *  classification (and any suppression) is scoped to the individual PGQ
 *  statement in a multi-statement buffer — never the whole buffer. Strings
 *  and comments are skipped so a `;` inside them doesn't split a statement,
 *  mirroring the source-scanning approach the CTE-body rules (R1/R2) use. */
function statementRangeCovering(
  source: string,
  range: OffsetRange,
): { start: number; end: number } {
  let segStart = 0;
  let i = 0;
  while (i < source.length) {
    const skipped = skipNonCode(source, i);
    if (skipped > i) {
      i = skipped;
      continue;
    }
    if (source.charCodeAt(i) === 0x3b /* ; */) {
      if (i >= range.start) return { start: segStart, end: i };
      segStart = i + 1;
    }
    i += 1;
  }
  return { start: segStart, end: source.length };
}

// ─────────────────────────────────────────────────────────────────────────
// Line/column → offset conversion
// ─────────────────────────────────────────────────────────────────────────

function computeLineStarts(source: string): number[] {
  const starts: number[] = [0];
  for (let i = 0; i < source.length; i += 1) {
    if (source.charCodeAt(i) === 0x0a /* \n */) {
      starts.push(i + 1);
    }
  }
  return starts;
}

function lineColToOffset(
  line: number,
  column: number,
  lineStarts: readonly number[],
  sourceLength: number,
): number {
  if (line < 1) return 0;
  const idx = Math.min(line - 1, lineStarts.length - 1);
  const base = lineStarts[idx];
  const offset = base + Math.max(0, column - 1);
  return Math.min(offset, sourceLength);
}

type OffsetRange = { readonly start: number; readonly end: number };

function monacoRangeToOffsets(
  marker: IMarkerData,
  lineStarts: readonly number[],
  sourceLength: number,
): OffsetRange | null {
  const start = lineColToOffset(
    marker.startLineNumber,
    marker.startColumn,
    lineStarts,
    sourceLength,
  );
  const end = lineColToOffset(
    marker.endLineNumber,
    marker.endColumn,
    lineStarts,
    sourceLength,
  );
  if (start > end) return null;
  return { start, end };
}

// ─────────────────────────────────────────────────────────────────────────
// Error-recovery overlap detection
// ─────────────────────────────────────────────────────────────────────────

/** True iff any entry in `view` overlaps `range` (inclusive on both ends —
 *  matches the previous tree-walker's `endIndex < start || startIndex > end`
 *  exclusion). Entries with confidence === 0 are ignored, leaving room for
 *  a future "definitely-not-error" signal. */
function rangeOverlapsErrorRecovery(
  view: ReadonlyArray<ErrorRecoveryEntry>,
  range: OffsetRange,
): boolean {
  for (const entry of view) {
    if (entry.confidence <= 0) continue;
    if (entry.range.endOffset < range.start) continue;
    if (entry.range.startOffset > range.end) continue;
    return true;
  }
  return false;
}

// ─────────────────────────────────────────────────────────────────────────
// Snapshot identifier confidence check
// ─────────────────────────────────────────────────────────────────────────

const IDENTIFIER_RE = /[A-Za-z_][A-Za-z0-9_]*/g;

function snapshotKnowsIdentifier(
  snapshot: SchemaSnapshot,
  source: string,
  range: OffsetRange,
): boolean {
  if (range.end <= range.start) return false;
  const slice = source.slice(range.start, range.end);
  IDENTIFIER_RE.lastIndex = 0;
  const matches = slice.match(IDENTIFIER_RE);
  if (!matches || matches.length === 0) return false;

  const known = collectKnownIdentifiers(snapshot);
  for (const ident of matches) {
    if (known.has(ident.toLowerCase())) return true;
  }
  return false;
}

function collectKnownIdentifiers(snapshot: SchemaSnapshot): Set<string> {
  const set = new Set<string>();
  for (const t of snapshot.tables) {
    set.add(t.name.toLowerCase());
    for (const c of t.columns) set.add(c.name.toLowerCase());
  }
  for (const r of snapshot.routines) set.add(r.name.toLowerCase());
  for (const s of snapshot.schemas) set.add(s.toLowerCase());
  return set;
}

// ─────────────────────────────────────────────────────────────────────────
// MSSQL: OPENJSON-in-CTE pattern
// ─────────────────────────────────────────────────────────────────────────

function isInsideOpenJsonInsideCte(source: string, range: OffsetRange): boolean {
  const lower = source.toLowerCase();
  for (const cte of findCteBodies(source, lower)) {
    if (range.start < cte.start || range.end > cte.end) continue;
    if (containsOpenJsonWith(lower, cte.start, cte.end)) {
      return true;
    }
  }
  return false;
}

function containsOpenJsonWith(
  lower: string,
  bodyStart: number,
  bodyEnd: number,
): boolean {
  // Find any `openjson(` followed (after a balanced `)`) by `with (` inside
  // the CTE body. We don't try to validate full SQL; the suppression scope
  // is "marker is inside a CTE that contains OPENJSON(...) WITH(...)" and
  // we only consume tokens within `[bodyStart, bodyEnd]`.
  let i = bodyStart;
  while (i < bodyEnd) {
    const idx = lower.indexOf('openjson', i);
    if (idx < 0 || idx >= bodyEnd) return false;
    // Must be a word boundary on the left.
    if (idx > bodyStart && isIdentifierCharCode(lower.charCodeAt(idx - 1))) {
      i = idx + 8;
      continue;
    }
    // Must be followed (skipping whitespace) by `(`.
    let j = idx + 8;
    while (j < bodyEnd && isWhitespaceCode(lower.charCodeAt(j))) j += 1;
    if (j >= bodyEnd || lower.charCodeAt(j) !== 0x28 /* ( */) {
      i = idx + 8;
      continue;
    }
    const closeArgs = findMatchingRparen(lower, j, bodyEnd);
    if (closeArgs < 0) return false;
    // Look for `with (` after the closing args paren, still inside the CTE.
    let k = closeArgs + 1;
    while (k < bodyEnd && isWhitespaceCode(lower.charCodeAt(k))) k += 1;
    if (k + 4 <= bodyEnd && lower.startsWith('with', k)) {
      const after = k + 4;
      // word boundary on the right of `with`
      if (after < bodyEnd && !isIdentifierCharCode(lower.charCodeAt(after))) {
        let m = after;
        while (m < bodyEnd && isWhitespaceCode(lower.charCodeAt(m))) m += 1;
        if (m < bodyEnd && lower.charCodeAt(m) === 0x28 /* ( */) {
          return true;
        }
      }
    }
    i = closeArgs + 1;
  }
  return false;
}

// ─────────────────────────────────────────────────────────────────────────
// PostgreSQL: RETURNING-in-CTE-DML pattern
// ─────────────────────────────────────────────────────────────────────────

function isInsideReturningInCteDml(source: string, range: OffsetRange): boolean {
  const lower = source.toLowerCase();
  for (const cte of findCteBodies(source, lower)) {
    if (range.start < cte.start || range.end > cte.end) continue;
    if (containsDmlWithReturning(lower, cte.start, cte.end)) {
      return true;
    }
  }
  return false;
}

function containsDmlWithReturning(
  lower: string,
  bodyStart: number,
  bodyEnd: number,
): boolean {
  const dmlVerbs = ['insert', 'update', 'delete'];
  let hasDml = false;
  for (const verb of dmlVerbs) {
    if (containsKeyword(lower, verb, bodyStart, bodyEnd)) {
      hasDml = true;
      break;
    }
  }
  if (!hasDml) return false;
  return containsKeyword(lower, 'returning', bodyStart, bodyEnd);
}

function containsKeyword(
  lower: string,
  word: string,
  start: number,
  end: number,
): boolean {
  let i = start;
  while (i < end) {
    const idx = lower.indexOf(word, i);
    if (idx < 0 || idx + word.length > end) return false;
    const left = idx > start ? lower.charCodeAt(idx - 1) : 0;
    const right =
      idx + word.length < end ? lower.charCodeAt(idx + word.length) : 0;
    if (!isIdentifierCharCode(left) && !isIdentifierCharCode(right)) {
      return true;
    }
    i = idx + word.length;
  }
  return false;
}

// ─────────────────────────────────────────────────────────────────────────
// Shared CTE-body scanner
// ─────────────────────────────────────────────────────────────────────────

type CteBody = OffsetRange;

/**
 * Locates the inner `(...)` body of every CTE in the source. A CTE body is
 * the parenthesized expression following `<ident> AS (`. We scan source
 * tokens and pair opening parens to their matching closes, ignoring string
 * literals and line/block comments. Returns ranges with `start` at the byte
 * AFTER the opening `(` and `end` at the byte BEFORE the matching `)`.
 *
 * Best-effort — we do not validate that the surrounding statement is a CTE
 * (`WITH ... AS (...)`); the OPENJSON/RETURNING shape inside the body is the
 * load-bearing signal. Standalone `OPENJSON(...) WITH(...)` outside any
 * `<ident> AS (` form is correctly excluded because there's no enclosing
 * paren-matched body.
 */
function findCteBodies(source: string, lower: string): CteBody[] {
  const bodies: CteBody[] = [];
  let i = 0;
  while (i < source.length) {
    const ch = source.charCodeAt(i);
    if (isWhitespaceCode(ch)) {
      i += 1;
      continue;
    }
    const skipped = skipNonCode(source, i);
    if (skipped > i) {
      i = skipped;
      continue;
    }
    // Look for `<ident> as (`
    if (isIdentifierStart(ch)) {
      const identEnd = readIdentifierEnd(source, i);
      let j = identEnd;
      // skip whitespace + comments
      j = skipNonCodeAndWs(source, j);
      if (j + 2 <= source.length && lower.startsWith('as', j)) {
        const afterAs = j + 2;
        if (
          afterAs >= source.length ||
          !isIdentifierCharCode(lower.charCodeAt(afterAs))
        ) {
          const k = skipNonCodeAndWs(source, afterAs);
          if (k < source.length && source.charCodeAt(k) === 0x28 /* ( */) {
            const close = findMatchingRparen(source, k, source.length);
            if (close > k) {
              bodies.push({ start: k + 1, end: close });
              i = k + 1;
              continue;
            }
          }
        }
      }
      i = identEnd;
      continue;
    }
    i += 1;
  }
  return bodies;
}

function readIdentifierEnd(source: string, start: number): number {
  // Optional leading quote/bracket — skip past the closing one.
  const ch = source.charCodeAt(start);
  if (ch === 0x22 /* " */ || ch === 0x60 /* ` */) {
    let j = start + 1;
    while (j < source.length) {
      if (source.charCodeAt(j) === ch) {
        if (source.charCodeAt(j + 1) === ch) {
          j += 2;
          continue;
        }
        return j + 1;
      }
      j += 1;
    }
    return source.length;
  }
  if (ch === 0x5b /* [ */) {
    let j = start + 1;
    while (j < source.length) {
      if (source.charCodeAt(j) === 0x5d /* ] */) {
        if (source.charCodeAt(j + 1) === 0x5d) {
          j += 2;
          continue;
        }
        return j + 1;
      }
      j += 1;
    }
    return source.length;
  }
  let j = start;
  while (j < source.length && isIdentifierCharCode(source.charCodeAt(j))) {
    j += 1;
  }
  return j;
}

function findMatchingRparen(source: string, openIdx: number, end: number): number {
  if (source.charCodeAt(openIdx) !== 0x28) return -1;
  let depth = 0;
  let i = openIdx;
  while (i < end) {
    const ch = source.charCodeAt(i);
    const skipped = skipNonCode(source, i);
    if (skipped > i) {
      i = skipped;
      continue;
    }
    if (ch === 0x28 /* ( */) {
      depth += 1;
      i += 1;
      continue;
    }
    if (ch === 0x29 /* ) */) {
      depth -= 1;
      if (depth === 0) return i;
      i += 1;
      continue;
    }
    i += 1;
  }
  return -1;
}

function skipNonCode(source: string, i: number): number {
  if (i >= source.length) return i;
  const ch = source.charCodeAt(i);
  // Line comment `-- … <EOL>`
  if (ch === 0x2d /* - */ && source.charCodeAt(i + 1) === 0x2d) {
    let j = i + 2;
    while (j < source.length && source.charCodeAt(j) !== 0x0a) j += 1;
    return j;
  }
  // Block comment `/* … */` (no nesting handled — sufficient for the
  // pattern detector at this layer)
  if (ch === 0x2f /* / */ && source.charCodeAt(i + 1) === 0x2a) {
    let j = i + 2;
    while (j + 1 < source.length) {
      if (source.charCodeAt(j) === 0x2a && source.charCodeAt(j + 1) === 0x2f) {
        return j + 2;
      }
      j += 1;
    }
    return source.length;
  }
  // Single-quoted string `'…''…'`
  if (ch === 0x27 /* ' */) {
    let j = i + 1;
    while (j < source.length) {
      if (source.charCodeAt(j) === 0x27) {
        if (source.charCodeAt(j + 1) === 0x27) {
          j += 2;
          continue;
        }
        return j + 1;
      }
      j += 1;
    }
    return source.length;
  }
  return i;
}

function skipNonCodeAndWs(source: string, i: number): number {
  let j = i;
  for (;;) {
    while (j < source.length && isWhitespaceCode(source.charCodeAt(j))) j += 1;
    const after = skipNonCode(source, j);
    if (after === j) return j;
    j = after;
  }
}

// ─────────────────────────────────────────────────────────────────────────
// Char predicates
// ─────────────────────────────────────────────────────────────────────────

function isIdentifierStart(code: number): boolean {
  return (
    (code >= 0x41 && code <= 0x5a) /* A-Z */ ||
    (code >= 0x61 && code <= 0x7a) /* a-z */ ||
    code === 0x5f /* _ */
  );
}

function isIdentifierCharCode(code: number): boolean {
  return (
    isIdentifierStart(code) ||
    (code >= 0x30 && code <= 0x39) /* 0-9 */
  );
}

function isWhitespaceCode(code: number): boolean {
  return code === 0x20 || code === 0x09 || code === 0x0a || code === 0x0d;
}
